import { useEffect, useMemo, useState } from 'react';

import { canMutate } from '@domain/auth/AdminUser';
import { useAuth } from '@app/hooks/useAuth';
import { ADMIN_API_BASE, apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { errorText } from '@infrastructure/api/errors';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { formatDateTime } from '@app/lib/format';

// Customers' toll-free registrations for US & Canada texting: the staff review queue
// (backend: integrations/tollfree/views_admin.py). Approve, send back with notes, buy the
// number (charged to the customer's balance at cost) and submit it to Telnyx; the monthly
// fee's state, and releasing a number (e.g. unpaid: fees.py emails staff after 30 days).
export interface RegistrationRow {
  id: string;
  created_at: string;
  updated_at: string;
  status: string;
  telnyx_status: string;
  business_name: string;
  customer_company: string;
  customer_email: string;
  contact_email: string;
  number: string;
}

interface Check { field: string; level: 'error' | 'warning'; message: string }
interface RegistrationDetail extends Record<string, unknown> {
  id: string;
  status: string;
  telnyx_status: string;
  telnyx_reason: string;
  staff_notes: string;
  business_name: string;
  customer: { id: string; company: string; email: string; balance: string; credit_limit: string } | null;
  images: { id: string; url: string; name: string; width: number; height: number }[];
  checks: Check[];
  payload_problems: string[];
  numbers: {
    id: string; phone_number: string; status: string; monthly_cost: string; telnyx_order_status: string;
    paid_until: string | null; fee_overdue_since: string | null; fee_paused: boolean;
  }[];
  events: { source: string; status: string; reason: string; at: string; actor: string }[];
  telnyx_live: boolean;
  number_monthly_cost: string;
  can: { approve: boolean; request_changes: boolean; buy_number: boolean; submit: boolean; release_number: boolean };
}

export const STATUSES: [string, string][] = [
  ['in_review', 'In review'], ['changes_requested', 'Changes requested'], ['approved', 'Approved'],
  ['submitted', 'Submitted'], ['verified', 'Verified'], ['rejected', 'Rejected'], ['draft', 'Draft'],
  ['cancelled', 'Cancelled'],
];
const TELNYX_STATUSES = ['In Progress', 'Waiting For Telnyx', 'Waiting For Vendor', 'Waiting For Customer',
  'Verified', 'Rejected'];
const STATUS_STYLE: Record<string, string> = {
  in_review: 'bg-blue-100 text-blue-800', changes_requested: 'bg-amber-100 text-amber-800',
  approved: 'bg-indigo-100 text-indigo-800', submitted: 'bg-indigo-100 text-indigo-800',
  verified: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800',
};
const label = (status: string) => STATUSES.find(([v]) => v === status)?.[1] ?? status;

// [label, field] per section of the detail view
const SECTIONS: [string, [string, string][]][] = [
  ['Business', [['Legal name', 'business_name'], ['Operating name', 'doing_business_as'], ['Type', 'entity_type'],
    ['Website', 'corporate_website'], ['Business number', 'registration_number'], ['Number type', 'registration_type'],
    ['Issued by', 'registration_country']]],
  ['Address', [['Street', 'address1'], ['Unit', 'address2'], ['City', 'city'], ['Province / state', 'region'],
    ['Postal code', 'postal_code'], ['Country', 'country']]],
  ['Contact', [['Name', 'contact_name'], ['Email', 'contact_email'], ['Phone', 'contact_phone']]],
  ['Texts', [['Use case', 'use_case'], ['Volume / month', 'message_volume'], ['Summary', 'use_case_summary'],
    ['Sample text', 'production_message_content'], ['HELP reply', 'help_message_response']]],
  ['Opt-in', [['How people agree', 'opt_in_workflow'], ['Keywords', 'opt_in_keywords'],
    ['Confirmation text', 'opt_in_confirmation_response'], ['Privacy policy', 'privacy_policy_url'],
    ['Terms', 'terms_url'], ['Adults only', 'age_gated_content'], ['Other information', 'additional_information']]],
];

const repo = new AdminListRepository<RegistrationRow>(apiClient, 'us-texting');

const columns: DataTableColumn<RegistrationRow>[] = [
  { id: 'updated_at', header: 'Updated', accessor: (r) => formatDateTime(r.updated_at) },
  {
    id: 'status', header: 'Status',
    accessor: (r) => (
      <span>
        <span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[r.status] ?? 'bg-slate-100 text-slate-600'}`}>
          {label(r.status)}
        </span>
        {r.telnyx_status && <span className="block text-xs text-slate-500">Telnyx: {r.telnyx_status}</span>}
      </span>
    ),
  },
  {
    id: 'business_name', header: 'Business',
    accessor: (r) => (
      <span>
        {r.business_name || '—'}
        <span className="block text-xs text-slate-500">{r.customer_company} · {r.customer_email}</span>
      </span>
    ),
  },
  { id: 'number', header: 'Number', accessor: (r) => r.number || '—' },
];

function value(detail: RegistrationDetail, field: string): string {
  if (field === 'contact_name') return `${detail.contact_first_name ?? ''} ${detail.contact_last_name ?? ''}`.trim();
  const v = detail[field];
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return v ? String(v) : '';
}

export function RegistrationDetailView({ id, canEdit, onChanged, onClose }: {
  id: string; canEdit: boolean; onChanged: () => void; onClose: () => void;
}) {
  const [detail, setDetail] = useState<RegistrationDetail | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get<RegistrationDetail>(`${ADMIN_API_BASE}/us-texting/${id}/`)
      .then((r) => setDetail(r.data)).catch((e) => setError(errorText(e)));
  }, [id]);

  async function act(name: string, body: Record<string, string> = {}) {
    setBusy(name);
    setError('');
    try {
      const r = await apiClient.post<RegistrationDetail>(`${ADMIN_API_BASE}/us-texting/${id}/${name}/`, body);
      setDetail(r.data);
      setNotes('');
      onChanged();
    } catch (e) {
      setError(errorText(e));
      // the history may show what happened (e.g. a number order with no answer)
      apiClient.get<RegistrationDetail>(`${ADMIN_API_BASE}/us-texting/${id}/`).then((r) => setDetail(r.data)).catch(() => {});
    } finally {
      setBusy('');
    }
  }

  function release() {
    const active = detail?.numbers.find((n) => n.status === 'active');
    if (!active) return;
    const ok = window.confirm(
      `Release ${active.phone_number} at Telnyx?\n\nThis can't be undone: the number stops costing money, `
      + 'the customer can no longer text from it, and a new number would need verification again (1-2 weeks).',
    );
    if (ok) act('release-number');
  }

  function buy() {
    if (!detail?.customer) return;
    const ok = window.confirm(
      `Buy a toll-free number for ${detail.business_name}?\n\n`
      + `$${detail.number_monthly_cost} (first month) is taken from the customer's balance `
      + `($${Number(detail.customer.balance).toFixed(2)}), then the number is ordered from Telnyx and submitted for verification.`,
    );
    if (ok) act('buy-number');
  }

  if (!detail) {
    return (
      <div className="mb-4 rounded border bg-white p-4 text-sm dark:bg-slate-900" data-testid="registration-detail">
        {error ? <p className="text-red-600" role="alert">{error}</p> : 'Loading…'}
      </div>
    );
  }

  const errors = detail.checks.filter((c) => c.level === 'error');
  const warnings = detail.checks.filter((c) => c.level === 'warning');
  return (
    <div className="mb-4 rounded border bg-white p-4 text-sm shadow-sm dark:bg-slate-900" data-testid="registration-detail">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold">{detail.business_name || 'No business name yet'}</h3>
          <p className="text-slate-500">
            {label(detail.status)}
            {detail.telnyx_status && ` · Telnyx: ${detail.telnyx_status}`}
            {detail.customer && ` · ${detail.customer.company} (${detail.customer.email}) · balance $${Number(detail.customer.balance).toFixed(2)}`}
          </p>
        </div>
        <button type="button" className="rounded border px-2 py-0.5 text-xs" onClick={onClose}>Close</button>
      </div>

      {!detail.telnyx_live && (
        <p className="mb-3 rounded bg-amber-50 p-2 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          Telnyx calls are switched off (TELNYX_TOLLFREE_LIVE): you can approve or send back, but no number can be bought yet.
        </p>
      )}
      {detail.telnyx_reason && (
        <p className="mb-3 rounded bg-red-50 p-2 text-red-800 dark:bg-red-950/40 dark:text-red-200">Telnyx: {detail.telnyx_reason}</p>
      )}
      {(errors.length > 0 || warnings.length > 0 || detail.payload_problems.length > 0) && (
        <ul className="mb-3 space-y-1" aria-label="Checks">
          {detail.payload_problems.map((p) => <li key={p} className="text-red-600">Telnyx needs: {p}</li>)}
          {errors.map((c) => <li key={c.field + c.message} className="text-red-600">{c.message}</li>)}
          {warnings.map((c) => <li key={c.field + c.message} className="text-amber-700 dark:text-amber-300">{c.message}</li>)}
        </ul>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {SECTIONS.map(([title, fields]) => (
          <section key={title}>
            <h4 className="mb-1 font-semibold">{title}</h4>
            <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1">
              {fields.map(([name, field]) => (
                <div key={field} className="contents">
                  <dt className="text-slate-500">{name}</dt>
                  <dd className="whitespace-pre-wrap break-words">{value(detail, field) || '—'}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
        <section>
          <h4 className="mb-1 font-semibold">Screenshots</h4>
          {detail.images.length === 0 ? <p className="text-slate-500">None</p> : (
            <ul className="space-y-1">
              {detail.images.map((image, i) => (
                <li key={image.id}>
                  <a className="text-blue-600 underline" href={image.url} target="_blank" rel="noopener noreferrer">
                    {image.name || `Screenshot ${i + 1}`}
                  </a>{' '}
                  <span className="text-slate-500">({image.width}×{image.height})</span>
                </li>
              ))}
            </ul>
          )}
          <h4 className="mb-1 mt-3 font-semibold">Numbers</h4>
          {detail.numbers.length === 0 ? <p className="text-slate-500">None</p> : (
            <ul>
              {detail.numbers.map((n) => (
                <li key={n.phone_number}>
                  {n.phone_number} · {n.status} · ${n.monthly_cost}/month
                  {n.status === 'active' && n.paid_until && ` · paid until ${formatDateTime(n.paid_until)}`}
                  {n.fee_overdue_since && (
                    <span className="block text-red-600">
                      Monthly fee unpaid since {formatDateTime(n.fee_overdue_since)}
                      {n.fee_paused ? ': paused, texts from it are refused' : ' (retried daily)'}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <h4 className="mb-1 mt-4 font-semibold">History</h4>
      <ul className="mb-4 space-y-0.5">
        {detail.events.map((e, i) => (
          <li key={i}>
            <span className="text-slate-500">{formatDateTime(e.at)}</span>
            {' · '}{e.source}: {label(e.status)}
            {e.actor && ` (${e.actor})`}
            {e.reason && ` · ${e.reason}`}
          </li>
        ))}
      </ul>

      {canEdit && (
        <div className="flex flex-wrap items-end gap-3">
          {detail.can.approve && (
            <button type="button" disabled={!!busy} onClick={() => act('approve')}
              className="rounded bg-blue-600 px-3 py-1.5 text-white disabled:opacity-50">
              {busy === 'approve' ? 'Approving…' : 'Approve'}
            </button>
          )}
          {detail.can.buy_number && (
            <button type="button" disabled={!!busy || !detail.telnyx_live} onClick={buy}
              title={detail.telnyx_live ? undefined : 'Telnyx calls are switched off'}
              className="rounded bg-green-700 px-3 py-1.5 text-white disabled:opacity-50">
              {busy === 'buy-number' ? 'Buying…' : `Buy number ($${detail.number_monthly_cost}) and submit`}
            </button>
          )}
          {detail.can.submit && (
            <button type="button" disabled={!!busy || !detail.telnyx_live} onClick={() => act('submit')}
              className="rounded border px-3 py-1.5 disabled:opacity-50">
              {busy === 'submit' ? 'Submitting…' : 'Submit to Telnyx again'}
            </button>
          )}
          {detail.can.release_number && (
            <button type="button" disabled={!!busy || !detail.telnyx_live} onClick={release}
              className="rounded border border-red-300 px-3 py-1.5 text-red-700 disabled:opacity-50">
              {busy === 'release-number' ? 'Releasing…' : 'Release number'}
            </button>
          )}
          {detail.can.request_changes && (
            <>
              <label className="flex min-w-[16rem] flex-1 flex-col gap-1">
                <span className="text-xs text-slate-500">What should the customer change? (they see this)</span>
                <textarea aria-label="Changes for the customer" value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={2} className="rounded border px-2 py-1" />
              </label>
              <button type="button" disabled={!!busy || !notes.trim()} onClick={() => act('request-changes', { notes })}
                className="rounded border px-3 py-1.5 disabled:opacity-50">
                {busy === 'request-changes' ? 'Sending…' : 'Send back for changes'}
              </button>
            </>
          )}
        </div>
      )}
      {error && <p className="mt-2 text-red-600" role="alert">{error}</p>}
    </div>
  );
}

export function UsTextingPage() {
  const user = useAuth((st) => st.user);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [telnyxStatus, setTelnyxStatus] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    if (status) f.status = status;
    if (telnyxStatus) f.telnyx_status = telnyxStatus;
    return f;
  }, [search, status, telnyxStatus]);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">US &amp; Canada texting</h2>
          <p className="text-sm text-slate-500">Customers' toll-free registrations: review, buy the number, submit to Telnyx.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select aria-label="Filter status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="rounded border px-2 py-1">
            <option value="">All statuses</option>
            {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select aria-label="Filter Telnyx status" value={telnyxStatus} onChange={(e) => setTelnyxStatus(e.target.value)}
            className="rounded border px-2 py-1">
            <option value="">Any Telnyx status</option>
            {TELNYX_STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <input type="search" placeholder="Search business, email, number" value={search}
            onChange={(e) => setSearch(e.target.value)} className="rounded border px-2 py-1" />
        </div>
      </div>
      {selected && (
        <RegistrationDetailView
          key={selected}
          id={selected}
          canEdit={canMutate(user)}
          onClose={() => setSelected(null)}
          onChanged={() => setReload((n) => n + 1)}
        />
      )}
      <DataTable<RegistrationRow>
        key={reload}
        columns={columns}
        fetcher={(p) => repo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => r.id}
        onRowClick={(r) => setSelected(r.id)}
        emptyState={<span data-testid="datatable-empty">No registrations yet.</span>}
      />
    </section>
  );
}
